import {Rule, Source} from 'Event/attendee-rules'
import styled from 'styled-components'
import React, {useState} from 'react'
import RuleComponent from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule'
import BackButton from 'lib/ui/Button/BackButton'
import Button from 'lib/ui/Button'
import RuleForm from 'Event/attendee-rules/RuleConfig/RuleList/RuleForm'
import {Label} from 'lib/ui/typography'
import Visible from 'lib/ui/layout/Visible'
import {Form} from 'organization/Event/FormsProvider'

export default function RuleList(props: {
  rules?: Rule[]
  close?: () => void
  onChange: (rules: Rule[]) => void
  onToggleRuleConfig?: () => void
  descriptionHidden?: boolean
  className?: string
  description?: string
  disabled?: boolean
  form?: Form
  backButtonLabel?: string
  sources?: Source[]
}) {
  const [ruleConfigVisible, setRuleConfigVisible] = useState(false)
  const toggleRuleConfig = () => {
    setRuleConfigVisible(!ruleConfigVisible)

    if (props.onToggleRuleConfig) {
      props.onToggleRuleConfig()
    }
  }

  const [selectedRuleIndex, setSelectedRuleIndex] = useState<number | null>(
    null,
  )

  const rules = props.rules || []

  const rule = selectedRuleIndex !== null ? rules[selectedRuleIndex] : null

  const addNewRule = () => {
    setSelectedRuleIndex(null)
    toggleRuleConfig()
  }

  const editRule = (index: number) => {
    setSelectedRuleIndex(index)
    toggleRuleConfig()
  }

  const createRule = (rule: Rule) => {
    const updated = [...rules, rule]
    props.onChange(updated)
  }

  const updateRule = (index: number, rule: Rule) => {
    const updated = rules.map((r, i) => (i === index ? rule : r))
    props.onChange(updated)
    setRuleConfigVisible(false)
  }

  const saveRule = (rule: Rule) => {
    if (selectedRuleIndex !== null) {
      updateRule(selectedRuleIndex, rule)
    } else {
      createRule(rule)
    }

    toggleRuleConfig()
  }

  const deleteRule = () => {
    toggleRuleConfig()
    const removed = rules.filter((_, i) => i !== selectedRuleIndex)
    props.onChange(removed)
  }

  if (ruleConfigVisible) {
    return (
      <RuleForm
        close={toggleRuleConfig}
        onDelete={deleteRule}
        onCreate={saveRule}
        rule={rule}
        description={props.description}
        form={props.form}
        sources={props.sources}
      />
    )
  }

  return (
    <div className={props.className}>
      <CloseRules
        onClick={props.close}
        backButtonLabel={props.backButtonLabel}
      />
      <Rules
        rules={rules}
        onEditRule={editRule}
        updateRule={updateRule}
        descriptionHidden={props.descriptionHidden}
        description={props.description}
        form={props.form}
      />
      <Button
        variant="outlined"
        fullWidth
        onClick={addNewRule}
        aria-label="add rule"
        disabled={props.disabled}
      >
        Add Rule
      </Button>
    </div>
  )
}

function CloseRules(props: {onClick?: () => void; backButtonLabel?: string}) {
  if (!props.onClick) {
    return null
  }
  return (
    <StyledBackButton
      onClick={props.onClick}
      aria-label="close rules config"
      backButtonLabel={props.backButtonLabel}
    />
  )
}

function Rules(props: {
  rules: Rule[]
  updateRule: (index: number, rule: Rule) => void
  onEditRule: (index: number) => void
  descriptionHidden?: boolean
  description?: string
  form?: Form
}) {
  const hasRules = props.rules.length > 0

  if (!hasRules && !props.descriptionHidden) {
    return <EmptyRulesText>No rules have been added</EmptyRulesText>
  }

  const updateAtIndex = (index: number) => (rule: Rule) =>
    props.updateRule(index, rule)

  return (
    <RulesContainer>
      <Visible when={!props.descriptionHidden}>
        <RulesDescription>
          {props.description
            ? props.description
            : 'Component will only be visible when'}{' '}
        </RulesDescription>
      </Visible>
      {props.rules.map((rule, index) => (
        <StyledRule
          key={index}
          rule={rule}
          isFirstRule={index === 0}
          onUpdate={updateAtIndex(index)}
          onEdit={() => props.onEditRule(index)}
          form={props.form}
        />
      ))}
    </RulesContainer>
  )
}

const RulesContainer = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[5]};
`

const StyledBackButton = styled(BackButton)`
  margin-bottom: ${(props) => props.theme.spacing[5]}!important;
`
const EmptyRulesText = styled.p`
  margin: ${(props) => props.theme.spacing[4]} 0;
  text-align: center;
`

const StyledRule = styled(RuleComponent)`
  margin-bottom: ${(props) => props.theme.spacing[3]};
`

const RulesDescription = styled(Label)`
  margin-bottom: ${(props) => props.theme.spacing[3]};
`
